import { Component, OnInit, DoCheck, Input, Output, EventEmitter } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Horse } from '../../../models/horse';
import { HorseService } from '../../../providers/horse.service';
import { SmsAbstractAppPage } from '../../sms-abstract-app-page';

function normalize(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

function levenshtein(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => i === 0 ? j : j === 0 ? i : 0)
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[a.length][b.length];
}

function isFuzzyNameMatch(a: string, b: string): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb || na.length < 3 || nb.length < 3) return false;
  if (na === nb) return true;
  if (na.length >= 4 && nb.length >= 4 && (na.startsWith(nb) || nb.startsWith(na))) return true;
  if (levenshtein(na, nb) <= 2) return true;
  return false;
}

@Component({
  standalone: false,
  selector: 'app-modal-add-horse',
  templateUrl: './modal-add-horse.component.html',
  styleUrls: ['./modal-add-horse.component.css']
})
export class ModalAddHorseComponent extends SmsAbstractAppPage implements OnInit, DoCheck {
  @Input() horse: Horse;
  @Output() horse_saved$: EventEmitter<Horse> = new EventEmitter<Horse>();
  @Output() existing_horse_selected$: EventEmitter<Horse> = new EventEmitter<Horse>();

  horse_list: Horse[] = [];
  name_matches: Horse[] = [];
  id_conflicts: Horse[] = [];

  private _lastCheckedKey = '';

  constructor(
    public activeModal: NgbActiveModal,
    private horseService: HorseService
  ) {
    super();
  }

  ngOnInit() {
    this.horseService.getList().then(horses => {
      this.horse_list = horses;
      this.runDuplicateCheck();
    }).catch(() => {
      // non-critical — duplicate check silently degrades if list unavailable
    });
  }

  ngDoCheck() {
    if (!this.horse) return;
    const key = [
      this.horse.name,
      this.horse.usef_number,
      this.horse.ec_number,
      this.horse.fei_id,
      this.horse.ushja_id,
    ].join('|');
    if (key !== this._lastCheckedKey) {
      this._lastCheckedKey = key;
      this.runDuplicateCheck();
    }
  }

  private runDuplicateCheck() {
    if (!this.horse_list.length) return;

    // Registry ID conflicts — definitive matches
    this.id_conflicts = this.horse_list.filter(h => {
      return (
        (this.horse.usef_number && h.usef_number && this.horse.usef_number === h.usef_number) ||
        (this.horse.ec_number   && h.ec_number   && this.horse.ec_number   === h.ec_number)   ||
        (this.horse.fei_id      && h.fei_id      && this.horse.fei_id      === h.fei_id)      ||
        (this.horse.ushja_id    && h.ushja_id    && this.horse.ushja_id    === h.ushja_id)
      );
    });

    // Fuzzy name matches — soft warning (exclude anything already flagged as an ID conflict)
    const conflictIds = new Set(this.id_conflicts.map(h => h.id));
    if ((this.horse.name || '').length >= 3) {
      this.name_matches = this.horse_list.filter(h =>
        !conflictIds.has(h.id) && isFuzzyNameMatch(this.horse.name, h.name)
      );
    } else {
      this.name_matches = [];
    }
  }

  idConflictReason(existing: Horse): string {
    const reasons: string[] = [];
    if (this.horse.usef_number && existing.usef_number === this.horse.usef_number)
      reasons.push(`USEF# ${existing.usef_number}`);
    if (this.horse.ec_number && existing.ec_number === this.horse.ec_number)
      reasons.push(`EC# ${existing.ec_number}`);
    if (this.horse.fei_id && existing.fei_id === this.horse.fei_id)
      reasons.push(`FEI ${existing.fei_id}`);
    if (this.horse.ushja_id && existing.ushja_id === this.horse.ushja_id)
      reasons.push(`USHJA# ${existing.ushja_id}`);
    return reasons.join(', ');
  }

  handleUseExisting(horse: Horse) {
    this.existing_horse_selected$.emit(horse);
  }

  handleSaveHorse(horse: Horse) {
    this.horse_saved$.emit(horse);
  }
}
